
import { Poll } from '@/types/poll';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useState } from 'react';

/**
 * Hook for updating linked insight cards using database functions and Edge Functions
 */
export const useCardUpdater = () => {
  const [isUpdating, setIsUpdating] = useState(false);

  /**
   * Updates the linked insight card if it exists by calling the Edge Function
   * @param poll The poll with updated votes
   * @returns Promise that resolves when the update is complete
   */
  const updateCard = async (poll: Poll) => {
    if (!poll.linked_card_id) return;
    
    setIsUpdating(true);
    try {
      console.log('Updating linked card stats for poll:', poll.id, 'linked to card:', poll.linked_card_id);
      
      // First, try using Edge Function directly for more reliable updates
      try {
        const { error: edgeFnError } = await supabase.functions.invoke('updateCardStats', {
          body: { poll_id: poll.id }
        });
        
        if (edgeFnError) {
          console.warn('Edge function call failed, falling back to RPC:', edgeFnError);
          throw edgeFnError;
        } else {
          console.log('Successfully updated card stats via Edge Function');
        }
      } catch (edgeFnError) {
        // Fallback to database RPC function
        console.log('Falling back to RPC function for card update');
        const { error } = await supabase.rpc('update_insight_card_stats', {
          input_poll_id: poll.id
        });
        
        if (error) {
          console.error('Error calling update_insight_card_stats function:', error);
          throw error;
        }
        
        console.log('Successfully updated linked card statistics via database function');
      }
      
      // After updating via RPC or Edge Function, fetch the updated card to confirm changes
      const { data: updatedCard, error: fetchError } = await supabase
        .from('insight_cards')
        .select('id, agree_percentage, total_pollsters')
        .eq('id', poll.linked_card_id)
        .single();
        
      if (fetchError) {
        console.error('Error fetching updated card:', fetchError);
      } else {
        console.log('Updated card values:', updatedCard);
      }
      
      // Show a toast notification about the update
      toast.success('Poll vote recorded and insight card updated', {
        id: `card-update-${poll.id}`,
        duration: 3000
      });
      
      return updatedCard;
    } catch (updateCardError) {
      console.error('Error updating linked card:', updateCardError);
      toast.error('Vote recorded but card statistics update failed');
      return null;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    updateCard,
    isUpdating
  };
};
